import { Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTabs } from '../hooks/useTabs';
import { useTheme } from '../hooks/useTheme';
import { useSystemVersion } from '../hooks/useSystemVersion';
import { MegaMenu } from './MegaMenu';
import { TabBar } from './TabBar';
import { NewVersionBanner } from './NewVersionBanner';
import { TabErrorBoundary } from './TabErrorBoundary';
import {
  HOME_TAB,
  MENU_COLUMNS,
  renderTabContent,
  tabFromMenuItem,
  tabFromPath,
  isKnownTabKind,
} from '../tabs/tabRegistry';
import type { TabInstance } from '../hooks/useTabs';

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

/**
 * Browserens egen højrekliksmenu spærres i hele appen (som referencens `.app`).
 * Tekstfelter er undtaget, så kopiér/indsæt og stavekontrol virker. Egne menuer
 * kalder selv preventDefault.
 */
function blockBrowserContextMenu(event: React.MouseEvent) {
  const target = event.target as Element | null;
  if (target?.closest?.('input, textarea, select, [contenteditable]')) return;
  event.preventDefault();
}

/**
 * Skallen: mørkeblå topbar med Menu-knap, fanelinje, tema-knap og version.
 *
 * Interaktionsmodellen er den samme som i den koerende planner
 * (`C:\vibing\nyhbplanner\HBPlanner v2.html`): menuen aabner sider som top-faner,
 * fanerne er instanser med parametre, og indholdet i ALLE aabne faner bliver
 * mounted – kun den aktive er synlig. Derfor overlever fx en halvt udfyldt
 * formular eller et kortudsnit et faneskift.
 */
export function AppShell() {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  // Systemversionen fra core; falder tilbage til build-versionen.
  const systemVersion = useSystemVersion();
  const navigate = useNavigate();
  const location = useLocation();

  const { tabs, activeId, activeTab, openTab, closeTab, setActive } = useTabs({
    storageKey: 'hbc-shell',
    // "Kort oversigt" kan lukkes, men aabnes igen, naar den sidste fane lukkes.
    fallbackTab: HOME_TAB,
    isValidTab: (tab) => isKnownTabKind(tab.kind),
  });

  /**
   * Område til Tickets = label paa den fane, BRUGEREN selv aktiverede senest foer
   * Tickets (menu, klik paa fane). Gendannede faner (genindlaesning) og dybe
   * URL'er saetter den ikke, saa feltet hellere er tomt end forkert.
   */
  const lastAreaLabel = useRef<string | undefined>(undefined);
  const getArea = useCallback(() => lastAreaLabel.current, []);

  /**
   * Faner, der lige er lukket. URL'en peger stadig paa den lukkede fane, indtil
   * navigeringen til naboen er slaaet igennem; uden dette naaede effekten
   * herunder at aabne fanen igen, saa X'et saa ud til ikke at virke.
   */
  const justClosed = useRef<Set<string>>(new Set());

  const open = useCallback(
    (tab: TabInstance) => {
      justClosed.current.delete(tab.id);
      if (tab.kind !== 'tickets') lastAreaLabel.current = tab.label;
      else if (activeTab && activeTab.kind !== 'tickets') lastAreaLabel.current = activeTab.label;
      openTab(tab);
      if (tab.path) navigate(tab.path);
    },
    [openTab, navigate, activeTab],
  );

  // URL -> fane: dybe links og genindlaesning aabner den rigtige fane.
  useEffect(() => {
    const fromUrl = tabFromPath(location.pathname, location.search);
    if (!fromUrl) {
      justClosed.current.clear();
      return;
    }
    if (justClosed.current.has(fromUrl.id)) return;
    justClosed.current.clear();
    if (fromUrl.id === activeId) return;
    openTab(fromUrl);
  }, [location.pathname, location.search, activeId, openTab]);

  function selectTab(id: string) {
    setActive(id);
    const tab = tabs.find((item) => item.id === id);
    if (tab && tab.kind !== 'tickets') lastAreaLabel.current = tab.label;
    if (tab?.path) navigate(tab.path);
  }

  function handleClose(id: string) {
    const index = tabs.findIndex((tab) => tab.id === id);
    if (index === -1) return;
    const remaining = tabs.filter((tab) => tab.id !== id);
    // Naermeste tilbagevaerende fane; er der ingen, aabner useTabs HOME_TAB igen.
    const neighbour = remaining[index] ?? remaining[index - 1] ?? remaining[0] ?? HOME_TAB;
    justClosed.current.add(id);
    // Naboen er ikke valgt af brugeren: er den lukkede fane forrige fane, glemmes den.
    if (tabs[index].label === lastAreaLabel.current) lastAreaLabel.current = undefined;
    closeTab(id);
    if (activeId === id && neighbour.path) navigate(neighbour.path);
  }

  /*
   * "Kort oversigt" har intet X, naar den er den eneste aabne fane – der skal
   * altid vaere mindst én fane. Er en anden fane den sidste, kan den lukkes:
   * saa aabnes Kort oversigt automatisk igen (useTabs' fallbackTab).
   */
  const visibleTabs = useMemo(
    () =>
      tabs.map((tab) =>
        tabs.length <= 1 && tab.id === HOME_TAB.id ? { ...tab, closable: false } : tab,
      ),
    [tabs],
  );

  return (
    <div className="app" onContextMenu={blockBrowserContextMenu}>
      <div className="topnav">
        <MegaMenu
          columns={MENU_COLUMNS}
          activeKind={activeTab?.kind}
          onSelect={(item) => open(tabFromMenuItem(item))}
        />

        <div className="topnav-center">
          <TabBar tabs={visibleTabs} activeId={activeId} onSelect={selectTab} onClose={handleClose} />
        </div>

        <div className="topnav-right">
          <button
            type="button"
            className="theme-toggle"
            title={theme === 'dark' ? 'Skift til lyst tema' : 'Skift til mørkt tema'}
            aria-label={theme === 'dark' ? 'Skift til lyst tema' : 'Skift til mørkt tema'}
            onClick={toggleTheme}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>
          <span className="app-version">HBCare Planner v. {systemVersion}</span>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              logout('Du er logget ud.');
              navigate('/login', { replace: true });
            }}
          >
            Log ud
          </button>
        </div>
      </div>

      <NewVersionBanner />

      <div className="main">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className="tab-page"
            role="tabpanel"
            aria-label={tab.label}
            hidden={tab.id !== activeId}
          >
            <TabErrorBoundary>
              <Suspense fallback={<div className="loading-note">Henter…</div>}>
                {renderTabContent(tab, { openTab: open, getArea })}
              </Suspense>
            </TabErrorBoundary>
          </div>
        ))}
      </div>
    </div>
  );
}
